import React, { useState } from 'react'
import { DataTable, Column } from '../../components/Datatable/Datatable'
import { Button } from '../../components/Button/Button'
import { Inventory } from 'domain/entities/Inventory'
import { useDeleteInventory } from 'presentation/hooks/inventory/useDeleteInventory'
import ConfirmationModal from 'presentation/components/Modals/ConfirmationModal/ConfirmationModal'

type InventoryListProps = {
  onEdit: (inventory: Inventory) => void
  refetch: () => void
  data: Inventory[]
  loading: boolean
  displayedColumns?: string[]
  actionsAccessor?: (row: Inventory) => React.ReactNode
}

export const InventoryList: React.FC<InventoryListProps> = ({
  onEdit,
  refetch,
  data,
  loading,
  displayedColumns,
  actionsAccessor,
}) => {
  const { deleteInventory } = useDeleteInventory()
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleteTitle, setDeleteTitle] = useState<string | null>(null)

  const handleDelete = async () => {
    if (deleteId) {
      try {
        await deleteInventory(deleteId)
        refetch()
        handleCloseConfirmationModal()
      } catch (e) {
        console.error(e)
      }
    }
  }

  const handleOpenConfirmationModal = (id: string, title: string) => {
    setDeleteId(id)
    setDeleteTitle(title)
    setIsConfirmationModalOpen(true)
  }

  const handleCloseConfirmationModal = () => {
    setDeleteId(null)
    setDeleteTitle(null)
    setIsConfirmationModalOpen(false)
  }

  if (loading) return <div>Loading…</div>

  if (data.length === 0) return <div>No Inventory available</div>

  const defaultColumns = ['Title', 'Created At', 'Actions']
  const defaultActionsAccessor = (c: Inventory) => (
    <>
      <Button variant="edit" onClick={() => onEdit(c)}>
        Edit
      </Button>{' '}
      <Button
        variant="delete"
        onClick={() => handleOpenConfirmationModal(c.id, c.title)}
      >
        Delete
      </Button>
    </>
  )

  const columns: Column<Inventory>[] = [
    { header: 'Title', accessor: 'title', className: 'text-left' },
    {
      header: 'Created At',
      accessor: (c) => new Date(c.createdAt * 1_000).toLocaleDateString(),
    },
    {
      header: 'Actions',
      accessor: actionsAccessor || defaultActionsAccessor,
    },
  ]

  const isValidColumn = (column: Column<Inventory>) => {
    if (displayedColumns) return displayedColumns.includes(column.header)
    else return defaultColumns.includes(column.header)
  }

  return (
    <>
      <DataTable
        columns={columns.filter(isValidColumn)}
        data={data}
        rowKey={(c) => c.id}
      />
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        bodyContent={
          <>
            <p>
              Are you sure you want to permanently delete <b>{deleteTitle}</b>?
              This action cannot be undone.
            </p>
          </>
        }
        footerContent={
          <>
            <Button
              variant="cancel"
              onClick={() => handleCloseConfirmationModal()}
            >
              Cancel
            </Button>
            <Button variant="delete" onClick={() => handleDelete()}>
              Delete
            </Button>
          </>
        }
      />
    </>
  )
}
